/**
 * src/components/games/launchers/CyberChessSoloLauncher.tsx
 *
 * Single-player (solo) launcher for CyberChess. Wraps the existing
 * CyberChessActivity with `mode='single-player'` so the activity:
 *   - skips NetworkManager setup entirely (no voice channel required)
 *   - sets the local player as host with white pieces
 *   - triggers `aiService.getBestMove(...)` whenever it's black's turn
 *   - gates `recordChessResult` and peer broadcasts behind mode==='multiplayer',
 *     so solo matches don't write to the leaderboards table (which would
 *     fail FK constraints on a non-existent AI profile row).
 *
 * This used to self-register a SECOND manifest ('chess-solo'), which made
 * CyberChess appear twice in the arcade picker. It no longer registers
 * anything: the single 'chess' manifest now declares `multiplayer: true`
 * AND `solo: true`, and GameManager dispatches here via SOLO_COMPONENTS
 * when the user picks Solo on the CyberChess tile. The multiplayer route
 * still resolves to CyberChessActivity directly via STATIC_COMPONENTS with
 * `mode` defaulting to 'multiplayer'.
 */
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import CyberChessActivity from '../CyberChessActivity';
import type { GameRuntimeProps } from '../../../game/framework/types';

const CyberChessSoloActivity: React.FC<GameRuntimeProps> = ({
  onExit,
  onChange,
  playerSelf,
  notify,
}) => {
  // Lifecycle ping mirrors IframeGameActivity / DrawTogetherSoloLauncher.
  useEffect(() => {
    onChange?.('chess');
    return () => onChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative w-full h-full bg-[#040711] text-white flex flex-col overflow-hidden">
      <CyberChessActivity
        mode="single-player"
        playerSelf={playerSelf}
        onExit={onExit}
        notify={notify}
        onChange={onChange}
      />

      {/*
        Floating Exit button — CyberChessActivity already renders its own
        Exit in multiplayer; in solo mode we still surface ours as a top-
        row redundant control so a user who's mid-AI-think can exit without
        hunting through the inner header.
      */}
      <button
        onClick={onExit}
        aria-label="Exit CyberChess solo"
        className="absolute top-3 right-3 z-50 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-bold uppercase tracking-widest hover:bg-red-500/20 transition-colors flex items-center gap-2 shadow-lg backdrop-blur-md"
      >
        <X size={14} />
        Exit
      </button>
    </div>
  );
};

export default CyberChessSoloActivity;
